import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { CardNB, ButtonNB, IconNB } from '../../../components/ui-nb';
import SparePartStatusBadge from '../../../components/spare-parts/SparePartStatusBadge';
import { getErrorMessage } from '../../../lib/errors';

interface SparePartData {
  id: string;
  order_id: string;
  description: string;
  status: string;
  tracking_number: string | null;
  created_at: string;
  updated_at: string;
  orders?: { release_code: string; id: string };
}

const STATUS_TRANSITIONS: Record<string, { next: string; label: string } | null> = {
  sin_pedir: { next: 'pedido', label: 'Marcar como pedido' },
  pedido: { next: 'recibido', label: 'Marcar como recibido' },
  recibido: null,
};

export default function SparePartDetail() {
  const { id, lang } = useParams();
  const navigate = useNavigate();
  const [sparePart, setSparePart] = useState<SparePartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [editTracking, setEditTracking] = useState('');
  const [markAsOrdered, setMarkAsOrdered] = useState(false);

  const fetchSparePart = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('spare_parts')
        .select(`
          *,
          orders(id, release_code)
        `)
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      if (!data) throw new Error('Spare part not found');

      setSparePart(data as SparePartData);
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to load spare part');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSparePart();
  }, [id]);

  // Open edit mode — populate fields from current data
  const handleStartEdit = () => {
    if (!sparePart) return;
    setEditDescription(sparePart.description);
    setEditTracking(sparePart.tracking_number || '');
    setMarkAsOrdered(false);
    setError(null);
    setIsEditing(true);
  };

  // Cancel edit — discard changes
  const handleCancelEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  // Save edits
  const handleSaveEdit = async () => {
    if (!sparePart || saving) return;
    const trimmedDesc = editDescription.trim();
    if (!trimmedDesc) {
      setError('Description cannot be empty');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updates: Record<string, unknown> = {
        description: trimmedDesc,
        tracking_number: editTracking.trim() || null,
      };

      // Auto-transition: tracking number added + was sin_pedir → mark as pedido
      if (markAsOrdered && sparePart.status === 'sin_pedir') {
        updates.status = 'pedido';
      }

      const { error: updateError } = await supabase
        .from('spare_parts')
        .update(updates)
        .eq('id', sparePart.id);

      if (updateError) throw updateError;

      setIsEditing(false);
      await fetchSparePart();
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusTransition = async (newStatus: string) => {
    if (!sparePart || updating) return;
    setUpdating(true);

    try {
      const { error: updateError } = await supabase
        .from('spare_parts')
        .update({ status: newStatus })
        .eq('id', sparePart.id);

      if (updateError) throw updateError;

      await fetchSparePart();
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <p className="text-nb-text-muted p-8">Loading spare part...</p>;
  if (error && !sparePart) return <p className="text-nb-red p-8">{error}</p>;
  if (!sparePart) return <p className="text-nb-text-muted p-8">Spare part not found</p>;

  const transition = STATUS_TRANSITIONS[sparePart.status];
  const isTerminal = sparePart.status === 'recibido';
  const showOrderedSuggestion = isEditing && sparePart.status === 'sin_pedir' && editTracking.trim().length > 0;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6 flex items-center gap-3">
        <ButtonNB variant="secondary" size="sm" onClick={() => navigate(-1)}>
          <IconNB name="chevronLeft" size={16} />
          Back
        </ButtonNB>
        <h1 className="text-2xl font-bold text-nb-black flex-1">
          Spare Part Detail
        </h1>
      </div>

      {/* Main Information Card */}
      <CardNB className="p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase">
            Information
          </h2>
          {!isEditing && (
            <ButtonNB variant="secondary" size="sm" onClick={handleStartEdit}>
              <IconNB name="settings" size={14} />
              Edit
            </ButtonNB>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 border-2 border-nb-red bg-nb-red/10">
            <p className="text-sm text-nb-red">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Description */}
          <div className="sm:col-span-2">
            <p className="text-xs text-nb-text-muted mb-1">Description</p>
            {isEditing ? (
              <input
                type="text"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="w-full px-3 py-2 text-nb-black border-2 border-nb-black bg-white focus:outline-none focus:border-nb-yellow font-nb"
                placeholder="Spare part description"
                autoFocus
              />
            ) : (
              <p className="font-nb font-medium text-nb-black">{sparePart.description}</p>
            )}
          </div>

          {/* Status */}
          <div>
            <p className="text-xs text-nb-text-muted mb-1">Status</p>
            <SparePartStatusBadge status={sparePart.status} />
          </div>

          {/* Tracking Number */}
          <div>
            <p className="text-xs text-nb-text-muted mb-1">Tracking Number</p>
            {isEditing ? (
              <input
                type="text"
                value={editTracking}
                onChange={(e) => setEditTracking(e.target.value)}
                className="w-full px-3 py-2 text-nb-black border-2 border-nb-black bg-white focus:outline-none focus:border-nb-yellow font-nb"
                placeholder="Tracking code (optional)"
              />
            ) : (
              <p className="text-nb-black">{sparePart.tracking_number || '—'}</p>
            )}
          </div>

          <div>
            <p className="text-xs text-nb-text-muted mb-1">Created</p>
            <p className="text-nb-black">
              {new Date(sparePart.created_at).toLocaleDateString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-nb-text-muted mb-1">Updated</p>
            <p className="text-nb-black">
              {new Date(sparePart.updated_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Auto-suggest: mark as ordered when tracking added to sin_pedir */}
        {showOrderedSuggestion && (
          <div className="mt-4 p-3 border-3 border-nb-yellow bg-nb-yellow/10">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={markAsOrdered}
                onChange={(e) => setMarkAsOrdered(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-nb-black"
              />
              <div>
                <p className="font-nb font-bold text-sm text-nb-black">
                  Mark as ordered?
                </p>
                <p className="text-xs text-nb-text-muted">
                  Adding a tracking number usually means the part has been ordered. Save and change status to "Ordered" automatically.
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Edit action buttons */}
        {isEditing && (
          <div className="flex gap-3 mt-4 pt-4 border-t-2 border-nb-black">
            <ButtonNB
              variant="primary"
              onClick={handleSaveEdit}
              loading={saving}
              disabled={saving}
            >
              Save Changes
            </ButtonNB>
            <ButtonNB
              variant="secondary"
              onClick={handleCancelEdit}
              disabled={saving}
            >
              Cancel
            </ButtonNB>
          </div>
        )}
      </CardNB>

      {/* Actions Card — status transitions */}
      <CardNB className="p-6 mb-6">
        <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase mb-4">
          Actions
        </h2>
        {isTerminal ? (
          <p className="text-nb-text-muted text-sm">
            This spare part has been received. No further actions available.
          </p>
        ) : transition ? (
          <ButtonNB
            variant="primary"
            onClick={() => handleStatusTransition(transition.next)}
            disabled={updating}
            loading={updating}
          >
            {transition.label}
          </ButtonNB>
        ) : null}
      </CardNB>

      {/* Parent Order Card */}
      <CardNB className="p-6">
        <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase mb-4">
          Parent Order
        </h2>
        {sparePart.orders ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-nb-text-muted mb-1">Order</p>
              <p className="font-nb font-medium text-nb-black">
                {sparePart.orders.release_code || 'Order'}
              </p>
            </div>
            <Link
              to={`/${lang}/admin/orders/${sparePart.order_id}`}
              className="inline-flex items-center gap-2 font-nb font-bold text-sm text-nb-black border-3 border-nb-black px-4 py-2 rounded-[2px] hover:bg-nb-yellow transition-colors no-underline"
            >
              View Order
              <IconNB name="chevronRight" size={16} />
            </Link>
          </div>
        ) : (
          <p className="text-nb-text-muted text-sm">No linked order found.</p>
        )}
      </CardNB>
    </div>
  );
}
